/**
 * Emily Richards - Video Producer Portfolio
 * Main JavaScript with HLS video streaming and interactive features
 */

import Hls from 'hls.js';

// =====================================================
// HLS Video Manager
// =====================================================
class HLSVideoManager {
  constructor() {
    this.hlsInstances = new Map();
    this.projectCards = document.querySelectorAll('.project-card');
    this.init();
  }

  init() {
    this.projectCards.forEach(card => {
      const video = card.querySelector('video');
      const hlsSrc = video?.dataset.hlsSrc;

      if (!video || !hlsSrc) return;

      // Initialize HLS for this video
      this.setupHLS(video, hlsSrc);
    });
  }

  setupHLS(video, hlsSrc) {
    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 30
      });

      hls.loadSource(hlsSrc);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('HLS manifest loaded:', hlsSrc);
        // Disable any subtitle/caption tracks
        this.disableCaptions(video);
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.error('HLS fatal error:', data);
          // Try to recover
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
          }
        }
      });

      this.hlsInstances.set(video, hls);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      video.src = hlsSrc;
      video.addEventListener('loadedmetadata', () => {
        this.disableCaptions(video);
      });
    }
  }

  disableCaptions(video) {
    // Disable all text tracks (captions, subtitles)
    if (video.textTracks) {
      for (let i = 0; i < video.textTracks.length; i++) {
        video.textTracks[i].mode = 'hidden';
      }
    }
  }

  destroy() {
    this.hlsInstances.forEach((hls, video) => {
      hls.destroy();
    });
    this.hlsInstances.clear();
  }
}

// =====================================================
// Video Playback Controller with Full Controls
// =====================================================
class VideoPlayback {
  constructor() {
    this.projectCards = document.querySelectorAll('.project-card');
    this.controlsHideTimeout = null;
    this.init();
  }

  init() {
    this.projectCards.forEach(card => {
      const video = card.querySelector('video');
      const overlay = card.querySelector('.project-overlay');
      if (!video) return;

      // Initialize video controls
      this.initVideoControls(card, video);

      // CLICK TO PLAY - Main functionality (on overlay only)
      if (overlay) {
        overlay.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.pauseAllVideos();
          this.playVideo(card, video);
        });
      }

      // DOUBLE-CLICK FOR FULLSCREEN
      card.addEventListener('dblclick', (e) => {
        if (e.target.closest('.video-controls')) return;
        e.preventDefault();
        e.stopPropagation();
        if (!card.classList.contains('playing')) {
          this.playVideo(card, video);
        }
        this.toggleFullscreen(card, video);
      });

      // HOVER TO PREVIEW - Plays muted preview on hover
      card.addEventListener('mouseenter', () => {
        if (!card.classList.contains('playing')) {
          this.previewVideo(card, video);
        }
      });

      card.addEventListener('mouseleave', () => {
        if (!card.classList.contains('playing')) {
          this.stopPreview(card, video);
        }
      });

      // Video ended - reset state
      video.addEventListener('ended', () => {
        this.resetVideo(card, video);
      });

      // Update progress bar during playback
      video.addEventListener('timeupdate', () => {
        this.updateProgress(card, video);
      });

      // Update duration when metadata loads
      video.addEventListener('loadedmetadata', () => {
        this.updateTimeDisplay(card, video);
      });
    });

    // Click outside to close playing video
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.project-card')) {
        this.pauseAllVideos();
      }
    });

    // Intersection Observer for scroll behavior
    this.setupScrollBehavior();
  }

  initVideoControls(card, video) {
    // Play/Pause button in controls
    const controlsPlayBtn = card.querySelector('.controls-play-btn');
    if (controlsPlayBtn) {
      controlsPlayBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleVideo(card, video);
      });
    }

    // Progress bar seeking
    const progressContainer = card.querySelector('.progress-container');
    if (progressContainer) {
      progressContainer.addEventListener('click', (e) => {
        e.stopPropagation();
        const rect = progressContainer.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        video.currentTime = percent * video.duration;
      });
    }

    // Mute button
    const muteBtn = card.querySelector('.mute-btn');
    if (muteBtn) {
      muteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        video.muted = !video.muted;
        muteBtn.classList.toggle('muted', video.muted);
        const volumeSlider = card.querySelector('.volume-slider');
        if (volumeSlider) {
          volumeSlider.value = video.muted ? 0 : video.volume;
        }
      });
    }

    // Volume slider
    const volumeSlider = card.querySelector('.volume-slider');
    if (volumeSlider) {
      volumeSlider.addEventListener('input', (e) => {
        e.stopPropagation();
        video.volume = e.target.value;
        video.muted = e.target.value == 0;
        if (muteBtn) {
          muteBtn.classList.toggle('muted', video.muted);
        }
      });
      volumeSlider.addEventListener('click', (e) => e.stopPropagation());
    }

    // Fullscreen button
    const fullscreenBtn = card.querySelector('.fullscreen-btn');
    if (fullscreenBtn) {
      fullscreenBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleFullscreen(card, video);
      });
    }

    // Auto-hide controls after inactivity
    const videoControls = card.querySelector('.video-controls');
    if (videoControls) {
      card.addEventListener('mousemove', () => {
        if (card.classList.contains('playing')) {
          card.classList.remove('controls-hidden');
          clearTimeout(this.controlsHideTimeout);
          this.controlsHideTimeout = setTimeout(() => {
            card.classList.add('controls-hidden');
          }, 3000);
        }
      });
    }
  }

  updateProgress(card, video) {
    const progressBar = card.querySelector('.progress-bar');
    if (progressBar && video.duration) {
      const percent = (video.currentTime / video.duration) * 100;
      progressBar.style.width = `${percent}%`;
    }
    this.updateTimeDisplay(card, video);
  }

  updateTimeDisplay(card, video) {
    const timeDisplay = card.querySelector('.time-display');
    if (timeDisplay) {
      const current = this.formatTime(video.currentTime);
      const duration = this.formatTime(video.duration || 0);
      timeDisplay.textContent = `${current} / ${duration}`;
    }
  }

  formatTime(seconds) {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  toggleVideo(card, video) {
    if (card.classList.contains('playing') && !video.paused) {
      video.pause();
    } else if (card.classList.contains('playing') && video.paused) {
      video.play();
    } else {
      this.pauseAllVideos();
      this.playVideo(card, video);
    }
  }

  playVideo(card, video) {
    card.classList.remove('previewing');
    card.classList.add('playing');
    card.classList.remove('controls-hidden');
    
    // Check saved mute state or default to unmuted for user-initiated play
    const muteBtn = card.querySelector('.mute-btn');
    video.muted = muteBtn?.classList.contains('muted') || false;
    
    video.play().catch(err => {
      console.log('Playback prevented:', err);
      // Fallback to muted if autoplay blocked
      video.muted = true;
      if (muteBtn) muteBtn.classList.add('muted');
      video.play().catch(() => {});
    });

    // Start auto-hide timer
    this.controlsHideTimeout = setTimeout(() => {
      card.classList.add('controls-hidden');
    }, 3000);
  }

  toggleFullscreen(card, video) {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      const mediaContainer = card.querySelector('.project-media');
      if (mediaContainer.requestFullscreen) {
        mediaContainer.requestFullscreen();
      } else if (mediaContainer.webkitRequestFullscreen) {
        mediaContainer.webkitRequestFullscreen();
      }
    }
  }

  pauseVideo(card, video) {
    card.classList.remove('playing');
    card.classList.remove('controls-hidden');
    video.pause();
    clearTimeout(this.controlsHideTimeout);
  }

  resetVideo(card, video) {
    card.classList.remove('playing');
    card.classList.remove('controls-hidden');
    video.currentTime = 0;
    clearTimeout(this.controlsHideTimeout);
  }

  previewVideo(card, video) {
    card.classList.add('previewing');
    video.muted = true;
    video.currentTime = 0;
    video.play().catch(() => {});
  }

  stopPreview(card, video) {
    card.classList.remove('previewing');
    video.pause();
    video.currentTime = 0;
  }

  pauseAllVideos() {
    this.projectCards.forEach(card => {
      const video = card.querySelector('video');
      if (video && card.classList.contains('playing')) {
        this.pauseVideo(card, video);
      }
    });
  }

  setupScrollBehavior() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const card = entry.target;
        const video = card.querySelector('video');
        if (!video) return;

        if (!entry.isIntersecting && card.classList.contains('playing')) {
          this.pauseVideo(card, video);
        }
      });
    }, options);

    this.projectCards.forEach(card => observer.observe(card));
  }
}

// =====================================================
// Smooth Scroll Navigation
// =====================================================
class SmoothScroll {
  constructor() {
    this.navLinks = document.querySelectorAll('.nav-link[data-section]');
    this.sections = document.querySelectorAll('section[id]');
    this.header = document.querySelector('.header');
    this.init();
  }

  init() {
    this.navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('data-section');
        const targetSection = document.getElementById(targetId);

        if (targetSection) {
          const headerHeight = this.header ? this.header.offsetHeight : 0;
          const targetPosition = targetSection.offsetTop - headerHeight;

          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      });
    });

    window.addEventListener('scroll', () => this.updateActiveSection());
    this.updateActiveSection();
  }

  updateActiveSection() {
    const scrollPosition = window.scrollY + window.innerHeight / 3;

    this.sections.forEach(section => {
      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      const sectionId = section.getAttribute('id');

      if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
        this.navLinks.forEach(link => {
          link.classList.remove('active');
          if (link.getAttribute('data-section') === sectionId) {
            link.classList.add('active');
          }
        });
      }
    });
  }
}

// =====================================================
// Header Scroll Behavior
// =====================================================
class HeaderScroll {
  constructor() {
    this.header = document.querySelector('.header');
    this.lastScrollY = 0;
    this.ticking = false;

    if (!this.header) return;
    this.init();
  }

  init() {
    window.addEventListener('scroll', () => {
      if (!this.ticking) {
        requestAnimationFrame(() => {
          this.updateHeader();
          this.ticking = false;
        });
        this.ticking = true;
      }
    });
  }

  updateHeader() {
    const currentScrollY = window.scrollY;

    if (currentScrollY > 50) {
      this.header.classList.add('scrolled');
    } else {
      this.header.classList.remove('scrolled');
    }

    if (currentScrollY > this.lastScrollY && currentScrollY > 200) {
      this.header.classList.add('hidden');
    } else {
      this.header.classList.remove('hidden');
    }

    this.lastScrollY = currentScrollY;
  }
}

// =====================================================
// Scroll Reveal Animation
// =====================================================
class ScrollReveal {
  constructor() {
    this.elements = document.querySelectorAll('.project-card, .contact-link, .social-link');
    this.init();
  }

  init() {
    this.elements.forEach(el => el.classList.add('reveal'));

    const options = {
      root: null,
      rootMargin: '0px 0px -100px 0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, options);

    this.elements.forEach(el => observer.observe(el));
  }
}

// =====================================================
// Page Transitions
// =====================================================
class PageTransition {
  constructor() {
    this.overlay = document.querySelector('.page-transition');
    this.init();
  }

  init() {
    window.addEventListener('load', () => {
      document.body.classList.add('loaded');
    });

    document.querySelectorAll('a[href^="/"]').forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (href && !href.startsWith('#')) {
          e.preventDefault();
          this.transitionTo(href);
        }
      });
    });
  }

  transitionTo(url) {
    if (this.overlay) {
      this.overlay.classList.add('active');
      setTimeout(() => {
        window.location.href = url;
      }, 500);
    } else {
      window.location.href = url;
    }
  }
}

// =====================================================
// Parallax Effect for Hero
// =====================================================
class ParallaxHero {
  constructor() {
    this.hero = document.querySelector('.hero');
    this.heroContent = document.querySelector('.hero-content');

    if (!this.hero || !this.heroContent) return;
    this.init();
  }

  init() {
    window.addEventListener('scroll', () => {
      const scrollY = window.scrollY;
      const heroHeight = this.hero.offsetHeight;

      if (scrollY < heroHeight) {
        const opacity = 1 - (scrollY / heroHeight) * 0.5;
        const translateY = scrollY * 0.3;

        this.heroContent.style.opacity = opacity;
        this.heroContent.style.transform = `translateY(${translateY}px)`;
      }
    });
  }
}

// =====================================================
// Initialize All Features
// =====================================================
document.addEventListener('DOMContentLoaded', () => {
  // Initialize HLS video manager first
  new HLSVideoManager();

  // Initialize all other modules
  new VideoPlayback();
  new SmoothScroll();
  new HeaderScroll();
  new ScrollReveal();
  new PageTransition();
  new ParallaxHero();

  console.log('Emily Richards Portfolio - Initialized with HLS support');
});
